/**
 * Wo ein Absatz beginnt. (24.09.2026, Ticket 2558)
 *
 * ## Woher das kommt
 *
 * Martin, 24.09.: Kündigt Vespera vor einem Werkzeugaufruf etwas an und
 * kommt danach die Antwort, stehen beide Teile ohne Leerzeichen, Punkt oder
 * Absatz hintereinander — „… denn dafür gibt's noch keins.Beides angelegt:
 * Ticket 2556 …".
 *
 * Belegt in der Datenbank: Vom 17. bis 24.09. tragen 228 von 352
 * gespeicherten Antworten MIT Werkzeugkarte so eine Klebestelle
 * (Kleinbuchstabe, Satzzeichen, direkt danach ein großgeschriebenes Wort),
 * dagegen 1 von 249 OHNE Werkzeug. Die Antworten mit den drei Belegen des
 * Tickets liefen alle über die warme Sitzung
 * (`[ptt-cli-warm] OK … werkzeuge=3..5`).
 *
 * Die Stelle: `WarmSession.runLoop` in server/claude-sdk-session.ts hängt
 * jedes Text-Delta ohne Trenner an den Puffer, auch das erste Delta einer
 * neuen Antwortrunde nach einem Werkzeug. Das Modell schreibt vor dem
 * Werkzeug „…keins." und danach „Beides …", jeweils ohne Zwischenraum.
 *
 * Folge fürs Vorlesen: Für shared/satzgrenzen.ts ist „keins.Beides" keine
 * Satzgrenze (kein Zwischenraum), beide Sätze werden ein Stück.
 *
 * ## Die Regel
 *
 * Ein Absatz ist eine Leerzeile, also mindestens zwei Zeilenumbrüche im
 * Zwischenraum zwischen zwei Textteilen. Diese Datei ist die EINE Stelle,
 * an der das steht:
 *
 *   - `absatzFuge` sagt dem Server, was er einsetzen muss, damit der Text
 *     nach einem Werkzeug als eigener Absatz beginnt. Was schon an
 *     Zeilenumbrüchen da ist, zählt mit, es entsteht nie mehr als eine
 *     Leerzeile.
 *   - `beginntAbsatz` sagt der Vorlese-Schlange, ob der erste Text nach
 *     einem Werkzeug einen Absatz beginnt. Sie liest die Grenze aus dem
 *     TEXT, nicht aus dem Werkzeug-Ereignis — sonst gäbe es zwei Besitzer
 *     dafür, wo die Grenze liegt.
 *
 * Anzeige, gespeicherter Verlauf und Vorlesen bekommen denselben Text, weil
 * der Server die Fuge VOR dem Senden und VOR dem Puffern einsetzt.
 */

/** Zeilenumbrüche im Zwischenraum am ENDE eines Textes. */
function umbruecheAmEnde(text: string): number {
  let n = 0;
  for (let i = text.length - 1; i >= 0; i--) {
    const z = text[i] ?? "";
    if (z === "\n") n++;
    else if (!/\s/.test(z)) break;
  }
  return n;
}

/** Zeilenumbrüche im Zwischenraum am ANFANG eines Textes. */
function umbruecheAmAnfang(text: string): number {
  let n = 0;
  for (const z of text) {
    if (z === "\n") n++;
    else if (!/\s/.test(z)) break;
  }
  return n;
}

/**
 * Was zwischen `bisher` und `neu` eingesetzt werden muss, damit `neu` als
 * eigener Absatz beginnt.
 *
 * Leer, wenn noch nichts dasteht (eine Antwort beginnt nicht mit einer
 * Leerzeile) oder wenn die Leerzeile schon vorhanden ist.
 */
export function absatzFuge(bisher: string, neu: string): string {
  if (!/\S/.test(bisher)) return "";
  const vorhanden = umbruecheAmEnde(bisher) + umbruecheAmAnfang(neu);
  return "\n".repeat(Math.max(0, 2 - vorhanden));
}

/**
 * Beginnt mit `neu` ein Absatz, nachdem schon `bisher` dastand?
 *
 * Maßgeblich ist der Zwischenraum an der Naht: das Ende von `bisher` plus
 * der Anfang von `neu`. So ist es gleich, wie der Strom die Zeilenumbrüche
 * auf die Stücke verteilt hat („\n" + „\nText" zählt wie „\n\nText").
 *
 * Solange `neu` nur Zwischenraum ist, ist noch nichts entschieden.
 */
export function beginntAbsatz(bisher: string, neu: string): boolean {
  const erstes = neu.search(/\S/);
  if (erstes < 0 || !/\S/.test(bisher)) return false;
  return umbruecheAmEnde(bisher) + umbruecheAmAnfang(neu) >= 2;
}
